import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  Stack,
  Snackbar,
  Alert as MuiAlert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { collection, doc, setDoc, getDocs } from "firebase/firestore";
import TBAdminTopNav from "./TBAdminTopNav";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Signup = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    organization: "",
    designation: "",
    dob: "",
    email: "",
    password: "",
  });

  const [organizations, setOrganizations] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const snapshot = await getDocs(collection(db, "organizations"));
        const orgList = snapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
        }));
        setOrganizations(orgList);
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    };

    fetchOrganizations();
  }, []);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);
  const validatePassword = (password) => password.length >= 6;
  const is18OrOlder = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    const d = today.getDate() - birthDate.getDate();
    return age > 18 || (age === 18 && (m > 0 || (m === 0 && d >= 0)));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    const {
      firstName,
      lastName,
      organization,
      designation,
      dob,
      email,
      password,
    } = form;

    if (
      !firstName ||
      !lastName ||
      !organization ||
      !designation ||
      !dob ||
      !email ||
      !password
    ) {
      setSnackbarMessage("Please fill all fields.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!validateEmail(email)) {
      setSnackbarMessage("Invalid email.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!validatePassword(password)) {
      setSnackbarMessage("Password must be at least 6 characters.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (!is18OrOlder(dob)) {
      setSnackbarMessage("You must be at least 18 years old.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      await sendEmailVerification(user);
      const selectedOrg = organizations.find((org) => org.id === organization);
      const organizationName = selectedOrg ? selectedOrg.name : "";
      await setDoc(doc(db, "users", user.uid), {
        firstName,
        lastName,
         organizationId: organization,
          organizationName,
        designation,
        dob,
        email,
        createdAt: new Date().toISOString(),
        emailVerified: false,
      });

      setSnackbarMessage("Signup successful! Verification email sent.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setForm({
        firstName: "",
        lastName: "",
        organization: "",
        designation: "",
        dob: "",
        email: "",
        password: "",
      });
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <Container maxWidth="sm">
      <TBAdminTopNav />
      <Paper elevation={3} sx={{ p: 4, mt: 6, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom>
          Sign Up
        </Typography>
        <Box component="form" onSubmit={handleSignup}>
          <Stack spacing={2}>
            <TextField
              label="First Name"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              fullWidth
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Select Organization</InputLabel>
              <Select
                name="organization"
                value={form.organization}
                onChange={handleChange}
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Designation"
              name="designation"
              value={form.designation}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Date of Birth"
              type="date"
              name="dob"
              value={form.dob}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              fullWidth
              required
            />
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{ mt: 1 }}
            >
              Sign Up
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Signup;
